import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/design/design/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
import { Box } from '@primer/react';
import ComponentLayout from '~/src/layouts/component-layout';
import { AccessibilityLink } from '~/src/components/accessibility-link';
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const Caption = makeShortcode("Caption");
const DoDontContainer = makeShortcode("DoDontContainer");
const Do = makeShortcode("Do");
const Dont = makeShortcode("Dont");
const Text = makeShortcode("Text");
const CustomVideoPlayer = makeShortcode("CustomVideoPlayer");
const layoutProps = {
  _frontmatter
};
const MDXLayout = ComponentLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">





    <h2>{`Anatomy`}</h2>
    <img width="960" role="presentation" src="https://user-images.githubusercontent.com/2313998/187719332-3ef9aa9e-476f-4e5a-93fa-5fbd203ece4a.png" />
    <ol>
      <li parentName="ol"><strong parentName="li">{`Parent node`}</strong>{`: A node that hides and shows child nodes by expanding and collapsing`}</li>
      <li parentName="ol"><strong parentName="li">{`End node`}</strong>{`: A node with no children`}</li>
      <li parentName="ol"><strong parentName="li">{`Nesting level indicator lines`}</strong>{`: Each line represents a level of nesting depth`}</li>
      <li parentName="ol"><strong parentName="li">{`Chevron`}</strong>{`: Toggles a parent node expanded or collapsed and indicates expanded/collapsed state`}</li>
      <li parentName="ol"><strong parentName="li">{`Leading visual (optional)`}</strong>{`: A visual cue for additional context`}</li>
      <li parentName="ol"><strong parentName="li">{`Node label`}</strong>{`: The text label for the node`}</li>
      <li parentName="ol"><strong parentName="li">{`Trailing visual (optional)`}</strong>{`: Same as a leading visual, but at the end`}</li>
    </ol>
    <h2>{`Content`}</h2>
    <h3>{`Node label`}</h3>
    <p>{`Node labels should be a succinct title for whatever entity the node represents. For example, a file name.`}</p>
    <p>{`By default, labels are truncated to a single line. However, node labels may be wrapped to multiple lines in cases where the entire label must always be visible.`}</p>
    <p>{`When a node's text is truncated, it should still be accessible to users via a browser-native tooltip.`}</p>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <Box as="figure" role="presentation" m={0} mdxType="Box">
    <img width="456" role="presentation" alt="2 1 singleLineTruncation" src="https://user-images.githubusercontent.com/2313998/187719335-efd76d7a-40dc-4bc8-8053-64211dd97b51.png" />
    <Caption role="presentation" color="fg.subtle" mdxType="Caption">
      Single line truncation
    </Caption>
  </Box>
  <Box as="figure" role="presentation" m={0} mdxType="Box">
    <img width="456" role="presentation" alt="2 2 multiLineLabel" src="https://user-images.githubusercontent.com/2313998/187719336-d85f8886-75d1-4b74-9e7a-766dd1fbc29d.png" />
    <Caption role="presentation" color="fg.subtle" mdxType="Caption">
      Wrapping node label text
    </Caption>
  </Box>
    </Box>
    <h3>{`Leading and trailing visuals`}</h3>
    <p>{`Leading and trailing visuals may be used as visual cues that communicate more information about a node.`}</p>
    <p>{`If the leading or trailing visual contains useful information, make sure it has a screen-reader accessible text label. Note that the file and folder icons are not considered useful information, as the TreeView's underlying markup will communicate their presence.`}</p>
    <p>{`Either all nodes have leading visuals, or no nodes have leading visuals. Leading visuals must be accompanied by a text label.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img width="456" role="presentation" alt="3 1 DO-consistentLeadingViz" src="https://user-images.githubusercontent.com/2313998/187719338-9f860dfa-2110-4cf7-8d3b-583ff569605f.png" />
    <Caption role="presentation" color="fg.subtle" mdxType="Caption">
      Render all nodes with leading visuals and text labels or just with text labels
    </Caption>
  </Do>
  <Dont mdxType="Dont">
    <img width="456" role="presentation" alt="3 2 DONT-mixLeadingViz" src="https://user-images.githubusercontent.com/2313998/187719339-83fd7065-5b85-427f-8650-7fc0cae7214d.png" />
    <Caption role="presentation" color="fg.subtle" mdxType="Caption">
      Don't have a tree where only some nodes have leading visuals
    </Caption>
  </Dont>
    </DoDontContainer>
    <h2>{`Interaction`}</h2>
    <p>{`Keyboard users can expand or collapse nodes without activating them by using the left and right arrow keys.`}</p>
    <p>{`To give cursor users an equivalent experience, there are two click areas:`}</p>
    <ul>
      <li parentName="ul">{`clicking the chevron only expands or collapses the node`}</li>
      <li parentName="ul">{`clicking anywhere else will activate the node`}</li>
    </ul>
    <p>{`If the node cannot be activated, clicking anywhere on the node will expand it.`}</p>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <Box as="figure" role="presentation" m={0} mdxType="Box">
    <img width="456" role="presentation" alt="4 1 hoverActivatable" src="https://user-images.githubusercontent.com/2313998/187719340-6f62c500-cd70-48b2-8fcf-18a7b6ef3151.png" />
    <Caption role="presentation" color="fg.subtle" mdxType="Caption">Two click areas - toggle expanded or activate link</Caption>
  </Box>
  <Box as="figure" role="presentation" m={0} mdxType="Box">
    <img width="456" role="presentation" alt="4 2 hoverOnlyExpandable" src="https://user-images.githubusercontent.com/2313998/187719342-d4f9f6e9-6446-4479-846f-6d7c31fd832b.png" />
    <Caption color="fg.subtle" mdxType="Caption">One click area - toggle expanded</Caption>
  </Box>
    </Box>
    <p>{`Nodes may not contain any other interactive elements besides the chevron. Activating a node can only perform one action. For example, following a link.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img width="456" role="presentation" alt="5 1 DO-singleActionPerNode" src="https://user-images.githubusercontent.com/2313998/187719344-abc7cfeb-82b0-4279-96df-ae732828c063.png" />
    <Caption color="fg.subtle" mdxType="Caption">Limit nodes to a single action</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img width="456" role="presentation" alt="5 2 DONT-multiActionsPerNode" src="https://user-images.githubusercontent.com/2313998/187719346-0a3ad806-17a0-4414-a82b-7a1d78be7902.png" />
    <Caption color="fg.subtle" mdxType="Caption">Don't put other interactive elements in a node</Caption>
  </Dont>
    </DoDontContainer>
    <p>{`We have investigated the feasibility of supporting a context menu for each node, and that could be something we support in the future if we find we need it.`}</p>
    <h3>{`States`}</h3>
    <img width="960" role="presentation" alt="6 nodeStates" src="https://user-images.githubusercontent.com/2313998/187719347-b795a78e-a09e-4c19-9b90-1f181927e936.png" />
    <p>{`Nodes have different visual treatments to indicate their state and hint what kind of user input they're ready to receive. These styles are inherited from the `}<a parentName="p" {...{
        "href": "/components/action-list"
      }}>{`action list`}</a>{` component.`}</p>
    <h2>{`Nesting`}</h2>
    <p>{`Nodes are visually nested using whitespace and alignment.`}</p>
    <h3>{`Align leading icons`}</h3>
    <Box as="figure" display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} m={0} sx={{
      gap: 3
    }} mdxType="Box">
  <Box flex={1} mdxType="Box">
    Icons at the same level are center-aligned. The chevron should not break the alignment: parent nodes and child nodes
    keep their leading visuals aligned.
  </Box>
  <img width="456" role="presentation" alt="19 leadingVizAlignment" src="https://user-images.githubusercontent.com/2313998/187768704-08fac391-f331-4a47-9181-073f1360677c.png" />
    </Box>
    <h3>{`Indentation`}</h3>
    <Box as="figure" display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} m={0} sx={{
      gap: 3
    }} mdxType="Box">
  <Box flex={1} mdxType="Box">Each level of nesting increments the size of the indentation.</Box>
  <img width="456" role="presentation" alt="20 nodesWithIndentations" src="https://user-images.githubusercontent.com/2313998/187768706-1910025a-f69b-487f-987b-319f3022cd09.png" />
    </Box>
    <h3>{`Nesting level indicator lines`}</h3>
    <p>{`On devices with `}<inlineCode parentName="p">{`:hover`}</inlineCode>{` support, the nesting indicator lines fade in when the user mouses over the entire component, or when there's focus inside the component.`}</p>
    <p>{`On devices without `}<inlineCode parentName="p">{`:hover`}</inlineCode>{`, the nesting indicator lines appear at all times.`}</p>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <img width="456" role="presentation" alt="21 1 showLinesOnHover" src="https://user-images.githubusercontent.com/2313998/187768708-805527f9-3b7c-4f6b-ae37-e232f83916b6.png" />
  <img width="456" role="presentation" alt="21 2 alwaysShowLinesOnTouch" src="https://user-images.githubusercontent.com/2313998/187768711-2702a7aa-e902-4568-ab99-4570e28bf646.png" />
    </Box>
    <h3>{`Handling deeply nested nodes`}</h3>
    <p>{`Navigating through deeply nested nodes can be cumbersome and visually clunky. If accessing nodes deeper than 10 levels deep is a common interaction for your use-case, reconsider whether a tree view is the best pattern.`}</p>
    <p>{`Tree views are designed to be horizontally compact in order to support many levels of nesting and long node labels. This makes content truncation and horizontal overflow less likely to occur for deeply nested nodes, but infinite nesting makes those cases impossible to prevent entirely.`}</p>
    <p>{`There are some strategies to avoid a tree view that horizontally overflows it's container:`}</p>
    <ul>
      <li parentName="ul">{`Allow the tree view's container to be manually resized`}</li>
      <li parentName="ul">{`Once a certain depth is reached, start the tree view from a deeper parent node and provide a way to navigate back up the tree`}</li>
      <li parentName="ul">{`Remove leading and trailing visuals`}</li>
    </ul>
    <p>{`If a tree view is truly the best pattern for your use-case and none of the suggested strategies prevent horizontal overflow, opt for horizontal scrolling as a last resort instead of hiding overflowing content.`}</p>
    <h2>{`Usage`}</h2>
    <h3>{`Tree views are only for hierarchical lists`}</h3>
    <p>{`A tree view solves a very specific problem. It's not a multi-purpose tool like an `}<a parentName="p" {...{
        "href": "/components/action-list"
      }}>{`action list`}</a>{` or meant for site navigation like a `}<a parentName="p" {...{
        "href": "/components/nav-list"
      }}>{`nav list`}</a>{`.`}</p>
    <p>{`Tree views are used to communicate a hierarchical list of items, and allow a user to navigate through, select, and take action on one or more items. A comparable expereince would be a list of files in a code editor, or an operating system file explorer. While they may visually look like navigation, tree views have specific interaction modes and expectations for assistive technology that differs from other patterns. Trying to use a tree view for something that looks visually apprpriate, but is not functionally inline with its intended use case may cause confusion or an unusable experience, especially if the user cannot see the screen.`}</p>
    <Text as="p" m={0} mdxType="Text">
  Before reaching for a tree view, first make sure that:
    </Text>
    <Box as="ul" mt={0} mdxType="Box">
  <li>the items in the tree view represent a list</li>
  <li>the list items are likely to have a parent-child relationship with more than 1 level of nesting</li>
  <li>the expand and collapse behavior aids in navigation instead of just making it more complex</li>
    </Box>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
  <Text as="p" m={0} mdxType="Text">
    Some good cases for a tree view are:
  </Text>
  <Box as="ul" mt={0} mdxType="Box">
    <li>navigating the file structure of a repo</li>
    <li>navigating a codebase's symbols (types, constants, functions, etc) organized by their scope hierarchy</li>
  </Box>
      </Box>
      <Box flex={1} mdxType="Box">
  <Text as="p" m={0} mdxType="Text">
    A tree view would <strong>not</strong> be appropriate for:
  </Text>
  <Box as="ul" mt={0} mdxType="Box">
    <li>global sidebar navigation</li>
    <li>an FAQ that collapses answers under question headings</li>
  </Box>
      </Box>
    </Box>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img width="456" role="presentation" alt="7 1 DO-useForList" src="https://user-images.githubusercontent.com/2313998/187719349-c028a00f-0a27-4be2-bf67-815c2a036916.png" />
    <Caption color="fg.subtle" mdxType="Caption">Use a tree view for a list</Caption>
  </Do>
  <Dont mdxType="Dont">
    <img width="456" role="presentation" alt="7 2 DONT-useForAccordion" src="https://user-images.githubusercontent.com/2313998/187719350-de23e9ac-fd10-4e56-b2a4-aca93255adb7.png" />
    <Caption color="fg.subtle" mdxType="Caption">Don't use a tree view for expanding and collapsing sections of content</Caption>
  </Dont>
    </DoDontContainer>
    <h3>{`Expanding and collapsing nodes`}</h3>
    <p>{`A tree view can be a frustrating pattern if it forces users to spend a lot of time expanding and collapsing nodes to find what they're looking for.`}</p>
    <h4>{`Expanded by default`}</h4>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <figcaption>
    <p>When a child node is active, all of it's parent nodes should be expanded.</p>
    <p>If it's likely that a user will want to interact with all or most of the nodes in the tree, render the tree with all parent nodes expanded to start. For example: when reviewing a pull request, all of the changed files are shown to make it easy for reviewers to scan and navigate the changed files.</p>
  </figcaption>
  <img width="456" role="presentation" alt="8 openActiveParentNodes" src="https://user-images.githubusercontent.com/2313998/187719353-6708e315-28fb-422a-b65a-942ec963c36d.png" />
    </Box>
    <h4>{`Preserve expanded state when parent node is collapsed`}</h4>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <Box flex={1} mdxType="Box">
    If a user expands nested parent nodes and then collapses a parent node higher in the hierarchy, persist the expanded
    parent nodes lower in the hierarchy. It could be frustrating to have to re-expand the nodes that were already
    opened.
  </Box>
  <CustomVideoPlayer width="456" loop src="https://user-images.githubusercontent.com/2313998/187719354-305a5e91-0ea5-444a-a670-3903bdba3761.mp4" mdxType="CustomVideoPlayer" />
    </Box>
    <h4>{`Optionally combine parent nodes`}</h4>
    <Box as="figure" role="presentation" m={0} mdxType="Box">
  <Box as="figcaption" mb={3} mdxType="Box">
    If no end nodes are in the ancestry and the parent nodes cannot be activated, you may combine parent nodes into a
    single parent node to reduce the levels of nesting. Less levels of nesting make for a more compact tree and less
    nodes for the user to expand to get to child nodes.
  </Box>
  <img width="960" role="presentation" alt="10 combiningParentNodes" src="https://user-images.githubusercontent.com/2313998/187768688-4aeb4c79-5393-4e30-9da7-3a74b9d42257.png" />
    </Box>
    <h3>{`Asynchronously loading child nodes`}</h3>
    <h4>{`Show placeholder nodes (preferred)`}</h4>
    <Box as="figure" role="presentation" m={0} mdxType="Box">
  <Box as="figcaption" mb={3} mdxType="Box">
    When we know the number of loading child nodes, show "skeleton" nodes until the nodes finish loading. This reassures
    the user that their request is in progress and provides a minimal preview of what's loading.
  </Box>
  <img width="960" role="presentation" alt="11 skeletonLoadingNodes" src="https://user-images.githubusercontent.com/2313998/187719358-d3021980-4769-455d-9b7e-b295ffe5ccc6.png" />
    </Box>
    <h4>{`General loading indicator (fallback)`}</h4>
    <Box as="figure" role="presentation" m={0} mdxType="Box">
  <Box as="figcaption" mb={3} mdxType="Box">
    When we don't know the number of loading child nodes, show a generic loading indicator.
  </Box>
  <img width="960" role="presentation" alt="12 genericLoadingNodes" src="https://user-images.githubusercontent.com/2313998/187719359-bff335dc-fae5-4a82-9de9-752919309f61.png" />
    </Box>
    <h4>{`Handling loading errors`}</h4>
    <Box m={0} mdxType="Box">
  <div>
    <p>
      Inform users why the data cannot be retrieved and give them a path to resolve it. The error message cannot appear
      in the tree view because it is not semantically a node in the tree. Instead, the error message should appear in a{' '}
      <a href="/components/dialog">dialog</a> with an optional call-to-action that can resolve the error.
    </p>
    <p>
      If the user dismisses the dialog, focus should be moved back to the collapsed parent node. If the user clicks a
      call-to-action that attempts to load the child nodes again, focus should be moved to the node in the tree that
      communicates the child nodes are loading.
    </p>
    <p>
      If we don't have enough information to write a useful error message, it's ok to write something generic like
      "Couldn't load".
    </p>
  </div>
  <img width="960" role="presentation" alt="13 errorHandling" src="https://user-images.githubusercontent.com/2313998/188977489-cf88908a-2995-4c18-8f53-aa80a49ff724.png" />
    </Box>
-
    <h3>{`Composition`}</h3>
    <Box as="figure" role="presentation" m={0} mb={4} mdxType="Box">
  <Box as="figcaption" mb={3} mdxType="Box">
    A common pattern is to render a tree view in a split page layout where the tree view is in the left pane, and a view
    for the selected file is rendered in the main content area.
  </Box>
  <img width="960" role="presentation" alt="14 treeSplitPgLayout" src="https://user-images.githubusercontent.com/2313998/187719363-72304389-9464-42c3-9ff2-c2ef9cbef5ed.png" />
    </Box>
    <Box as="figure" role="presentation" m={0} mdxType="Box">
  <Box as="figcaption" mb={3} mdxType="Box">
    However, a tree view can also be rendered on it's own.
  </Box>
  <img width="960" role="presentation" alt="15 treeStandalone" src="https://user-images.githubusercontent.com/2313998/187719365-e179970c-808c-498c-908b-bd98df9442f8.png" />
    </Box>
    <h4>{`Examples`}</h4>
    <Box as="figure" role="presentation" m={0} mb={5} mdxType="Box">
  <img width="960" role="presentation" alt="16 treeViewAsFileExplorer" src="https://user-images.githubusercontent.com/2313998/188683239-692e680a-673c-42a3-a532-42d5a0df51c3.png" />
  <Caption as="figcaption" color="fg.subtle" mdxType="Caption">
    A tree view being rendered in a split page layout.
  </Caption>
    </Box>
    <Box as="figure" role="presentation" m={0} mdxType="Box">
  <img width="960" role="presentation" alt="17 treeViewOnItsOwn" src="https://user-images.githubusercontent.com/2313998/188683240-052b6e5d-529c-456c-acb2-f376aa8da17e.png" />
  <Caption as="figcaption" color="fg.subtle" mdxType="Caption">
    A tree view that replaces itself with the content of the activated node.
  </Caption>
    </Box>
    <h4>{`Provide sufficient vertical space`}</h4>
    <p>{`Be considerate of the amount of vertical space a tree view can take up when all of it's nodes are expanded.`}</p>
    <DoDontContainer mdxType="DoDontContainer">
  <Do mdxType="Do">
    <img width="456" role="presentation" alt="18 1 DO-renderWithEnoughSpace" src="https://user-images.githubusercontent.com/2313998/187768699-d97f4119-f989-487c-9d81-aa9253dbd580.png" />
    <Caption color="fg.subtle" mdxType="Caption">
      Render a tree view in an area with enough vertical space to comfortably browse the list, and doesn't push
      important content or controls below the bottom of the viewport
    </Caption>
  </Do>
  <Dont mdxType="Dont">
    <img width="456" role="presentation" alt="18 2 DONT-renderTooSmall" src="https://user-images.githubusercontent.com/2313998/187768702-2881810c-7d1d-4e36-ae85-dfc35cf95cda.png" />
    <Caption color="fg.subtle" mdxType="Caption">
      Don't render a tree view in an area that pushes down other content or forces users to scroll through small
      segments of the list
    </Caption>
  </Dont>
    </DoDontContainer>
    <h2>{`Accessibility`}</h2>
    <h3>{`Behavioral expectations`}</h3>
    <p>{`The TreeView replicates the experience of an operating system's file explorer capabilities. It is designed to allow a user to navigate and take action on groups of items listed in a hierarchical format. This is a different experience than website navigation, which has a different mental model, operational requirements, and usability expectations.`}</p>
    <h3>{`Label`}</h3>
    <Box m={0} mdxType="Box">
  <Box as="p" mdxType="Box">
    Tree views need an accessible name to be supplied, ideally via `aria-labelledby` pointing at an appropriate heading
    before the tree. If the tree view has enough surrounding context that it doesn't need a visible label, apply
    `sr-only` to the heading to hide it visually, but preserve the underlying programatic association.
  </Box>
  <img width="960" role="presentation" alt="25 labelledTreeView" src="https://user-images.githubusercontent.com/2313998/187957351-02c80d5a-c2a3-42bc-9811-7ee40e8f6cba.png" />
    </Box>
    <h3>{`Keyboard navigation`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Key(s)`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`Enter`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Performs the default action (e.g. onclick event) for the focused node which is to activate the link.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`Tab`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Moves focus outside of the tree view to the next focusable node.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`ArrowDown`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}><ul><li>{`Moves focus to the next node that is focusable without opening or closing a node.`}</li>{` `}<li>{`If focus is on the last node, does nothing.`}</li></ul></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`ArrowUp`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}><ul><li>{`Moves focus to the previous node that is focusable without opening or closing a node.`}</li><li>{`If focus is on the first node, does nothing.`}</li></ul></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`ArrowRight`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}><ul><li>{`When focus is on a closed node, opens the node; focus does not move.`}</li><li>{`When focus is on a open node, moves focus to the first child node.`}</li><li>{`When focus is on an end node, does nothing.`}</li></ul></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`ArrowLeft`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}><ul><li>{`When focus is on an open node, closes the node.`}</li><li>{`When focus is on a child node that is also either an end node or a closed node, moves focus to its parent node.`}</li><li>{`When focus is on a root node that is also either an end node or a closed node, does nothing.`}</li></ul></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`Home`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Moves focus to first node without opening or closing a node.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`End`}</Box></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Moves focus to the last node that can be focused without expanding any nodes that are closed.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`a-z`}</Box>{`, `}<Box as="kbd" backgroundColor="canvas.subtle" borderRadius={1} borderColor="border.default" borderWidth={1} borderStyle="solid" py={1} px={2} mdxType="Box">{`A-Z`}</Box>{`, all printable characters`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><ul><li>{`Focus moves to the next node with a name that starts with the typed character.`}</li><li>{`Search wraps to first node if a matching name is not found among the nodes that follow the focused node.`}</li><li>{`Search ignores nodes that are descendants of closed nodes.`}</li><li>{`Focus should not move if no nodes match the search.`}</li></ul></td>
        </tr>
      </tbody>
    </table>
    <h4>{`Typeahead behavior`}</h4>
    <Box display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
  <Box flex={1} mdxType="Box">
    <p>
      Focus moves to the next node with a name that starts with the typed character(s). Wait for 300ms from the last key
      press to stop searching.
    </p>
    <p>
      Typeahead behavior is case insensitive, and any printable character can be used, not just alphanumeric characters.
    </p>
    <p>
      The `Space` key is ignored because it's being reserved for toggling a tree view node's checkbox. Nodes with
      checkboxes are not supported yet, but they may in the future.
    </p>
  </Box>
  <CustomVideoPlayer width="456" loop src="https://user-images.githubusercontent.com/2313998/187719392-3693e45a-f108-4b78-84ef-0cfe4c7067f9.mp4" mdxType="CustomVideoPlayer" />
    </Box>
    <h3>{`Focus behavior`}</h3>
    <Box display="flex" alignItems="center" flexDirection={['column', 'column', 'column', 'column', 'row']} sx={{
      gap: 3
    }} mdxType="Box">
      <Box flex={1} mdxType="Box">
        <h4>{`Focus in`}</h4>
        <p>{`If moving focus into the TreeView for the first time, focus on the first node.`}</p>
        <p>{`If moving focus back into the TreeView after the user already interacted with it, focus the previously focused TreeView node.`}</p>
        <h4>{`Focus out`}</h4>
        <p>{`If activating a node causes new content to appear without a page refresh, focus should be moved to the new content. By default, focus should be moved to the first focusable control within the main content region. We should avoid skipping any content so that the user can easily go back to the TreeView if they activate a node by accident.`}</p>
        <p>{`If the first focusable element would cause a confusing experience for folks who listen to content using a screen reader, then an alternate element may be defined`}</p>
      </Box>
      <CustomVideoPlayer width="456" loop src="https://user-images.githubusercontent.com/2313998/187719387-44088bf3-5236-4b0e-955b-85918f35734f.mp4" mdxType="CustomVideoPlayer" />
    </Box>
    <h3>{`Minimum click target area for the chevron`}</h3>
    <Box as="figure" display="flex" flexDirection={['column', 'column', 'column', 'column', 'row']} m={0} sx={{
      gap: 3
    }} mdxType="Box">
  <Box as="figcaption" mdxType="Box">
    <p>
      The chevron has a generous click area to make it an easier target to hit, but it's kept compact to preserve
      horizontal space in deeply nested nodes.
    </p>
    <p>When we detect that the user is on a device with a coarse pointer, the click target is enlarged.</p>
  </Box>
  <img width="456" role="presentation" alt="24 minTouchTarget" src="https://user-images.githubusercontent.com/2313998/187769276-d946bbbf-0fdb-4d40-8614-63b4ee17027e.png" />
    </Box>
    <h3>{`Known accessibility issues (GitHub staff only)`}</h3>
 <AccessibilityLink label="TreeView" mdxType="AccessibilityLink" />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      